import React, { useState, useEffect } from 'react';
import { 
  Tag, Gift, Ticket, Plus, Trash2, Eye, Copy, Check, X,
  Calendar, Users, Search, Filter, Download, RefreshCw
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const AdminP2PCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showRedemptionsModal, setShowRedemptionsModal] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [filter, setFilter] = useState('all'); // all, active, inactive
  const [copiedCode, setCopiedCode] = useState(null);

  const [createForm, setCreateForm] = useState({
    tier_name: 'SILVER',
    quantity: 1,
    max_uses: 1,
    code: '',
    expires_days: 0
  });

  const [bulkForm, setBulkForm] = useState({
    tier_name: 'SILVER',
    quantity: 1,
    count: 10,
    max_uses: 1,
    expires_days: 0
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [couponsRes, tiersRes] = await Promise.all([
        api.get(`/admin/p2p/coupons${filter !== 'all' ? `?is_active=${filter === 'active'}` : ''}`),
        api.get('/p2p/tiers')
      ]);
      setCoupons(couponsRes.data);
      setTiers(tiersRes.data);
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      const data = {
        tier_name: createForm.tier_name,
        quantity: createForm.quantity,
        max_uses: createForm.max_uses || null,
        code: createForm.code || undefined,
        expires_at: createForm.expires_days > 0 
          ? new Date(Date.now() + createForm.expires_days * 24 * 60 * 60 * 1000).toISOString()
          : null
      };
      
      const response = await api.post('/admin/p2p/coupon', data);
      toast.success(`Coupon created: ${response.data.coupon.code}`);
      setShowCreateModal(false);
      setCreateForm({ tier_name: 'SILVER', quantity: 1, max_uses: 1, code: '', expires_days: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleBulkCreate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        tier_name: bulkForm.tier_name,
        quantity: bulkForm.quantity,
        count: bulkForm.count,
        max_uses: bulkForm.max_uses || 1,
        expires_at: bulkForm.expires_days > 0
          ? new Date(Date.now() + bulkForm.expires_days * 24 * 60 * 60 * 1000).toISOString()
          : null
      };
      
      const response = await api.post('/admin/p2p/coupons/bulk', data);
      toast.success(`Created ${response.data.codes.length} coupons!`);
      setShowBulkModal(false);
      setBulkForm({ tier_name: 'SILVER', quantity: 1, count: 10, max_uses: 1, expires_days: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupons');
    }
  };

  const handleToggleCoupon = async (coupon) => {
    try {
      await api.put(`/admin/p2p/coupon/${coupon.id}`, { is_active: !coupon.is_active });
      toast.success(`Coupon ${coupon.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };

  const handleViewRedemptions = async (coupon) => {
    try {
      const response = await api.get(`/admin/p2p/coupon/${coupon.id}/redemptions`);
      setRedemptions(response.data);
      setShowRedemptionsModal(coupon);
    } catch (error) {
      toast.error('Failed to fetch redemptions');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const exportCoupons = () => {
    const csv = coupons.map(c => `${c.code},${c.tier_name},${c.quantity},${c.max_uses || 'Unlimited'},${c.redemption_count},${c.is_active ? 'Active' : 'Inactive'}`).join('\n');
    const header = 'Code,Tier,Passes,Max Uses,Redeemed,Status\n';
    const blob = new Blob([header + csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'coupons.csv';
    a.click();
  };

  const TIER_COLORS = {
    SILVER: 'bg-gray-500',
    GOLD: 'bg-yellow-500',
    DIAMOND: 'bg-cyan-500',
    VIP: 'bg-purple-500',
    PREMIUM: 'bg-pink-500'
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Gift className="w-8 h-8 text-yellow-500" />
              Coupon Management
            </h1>
            <p className="text-muted-foreground mt-1">Create and manage listing pass coupons</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportCoupons}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-xl hover:bg-white/20"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => setShowBulkModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600"
            >
              <Plus className="w-4 h-4" />
              Bulk Create
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" />
              Create Coupon
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="glass-card p-4">
            <p className="text-sm text-muted-foreground">Total Coupons</p>
            <p className="text-2xl font-bold">{coupons.length}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-bold text-green-500">{coupons.filter(c => c.is_active).length}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-sm text-muted-foreground">Total Redemptions</p>
            <p className="text-2xl font-bold text-blue-500">{coupons.reduce((sum, c) => sum + (c.redemption_count || 0), 0)}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-sm text-muted-foreground">Passes Distributed</p>
            <p className="text-2xl font-bold text-purple-500">
              {coupons.reduce((sum, c) => sum + ((c.redemption_count || 0) * c.quantity), 0)}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          {['all', 'active', 'inactive'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-xl capitalize ${
                filter === f ? 'bg-primary text-primary-foreground' : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              {f}
            </button>
          ))}
          <button onClick={fetchData} className="ml-auto p-2 bg-white/5 rounded-xl hover:bg-white/10">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Coupons Table */}
        <div className="glass-card overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : coupons.length === 0 ? (
            <div className="text-center py-20">
              <Ticket className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No coupons found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-4 font-medium">Code</th>
                  <th className="text-left p-4 font-medium">Tier</th>
                  <th className="text-center p-4 font-medium">Passes</th>
                  <th className="text-center p-4 font-medium">Uses</th>
                  <th className="text-center p-4 font-medium">Redeemed</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-right p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {coupons.map(coupon => (
                  <tr key={coupon.id} className="hover:bg-white/5">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <code className="px-3 py-1 bg-white/10 rounded-lg font-mono text-sm">
                          {coupon.code}
                        </code>
                        <button
                          onClick={() => copyCode(coupon.code)}
                          className="p-1 hover:bg-white/10 rounded"
                        >
                          {copiedCode === coupon.code ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${TIER_COLORS[coupon.tier_name]}`}>
                        {coupon.tier_name}
                      </span>
                    </td>
                    <td className="p-4 text-center">{coupon.quantity}</td>
                    <td className="p-4 text-center">{coupon.max_uses || '∞'}</td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handleViewRedemptions(coupon)}
                        className="text-blue-500 hover:underline"
                      >
                        {coupon.redemption_count || 0}
                      </button>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        coupon.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                      }`}>
                        {coupon.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => handleToggleCoupon(coupon)}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          coupon.is_active 
                            ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30'
                            : 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                        }`}
                      >
                        {coupon.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Single Coupon Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Create Coupon</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateCoupon} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Pass Tier</label>
                <select
                  value={createForm.tier_name}
                  onChange={(e) => setCreateForm({ ...createForm, tier_name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                >
                  {tiers.map(t => <option key={t.id} value={t.name}>{t.name} (₹{t.price})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Passes per Redemption</label>
                <input
                  type="number"
                  min="1"
                  value={createForm.quantity}
                  onChange={(e) => setCreateForm({ ...createForm, quantity: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max Uses (0 = unlimited)</label>
                <input
                  type="number"
                  min="0"
                  value={createForm.max_uses}
                  onChange={(e) => setCreateForm({ ...createForm, max_uses: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Custom Code (optional)</label>
                <input
                  type="text"
                  value={createForm.code}
                  onChange={(e) => setCreateForm({ ...createForm, code: e.target.value.toUpperCase() })}
                  placeholder="Auto-generated if empty"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl uppercase"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Expires in (days, 0 = never)</label>
                <input
                  type="number"
                  min="0"
                  value={createForm.expires_days}
                  onChange={(e) => setCreateForm({ ...createForm, expires_days: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-semibold"
              >
                Create Coupon
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Bulk Create Coupons</h3>
              <button onClick={() => setShowBulkModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleBulkCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Pass Tier</label>
                <select
                  value={bulkForm.tier_name}
                  onChange={(e) => setBulkForm({ ...bulkForm, tier_name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                >
                  {tiers.map(t => <option key={t.id} value={t.name}>{t.name} (₹{t.price})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Number of Coupons to Create</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={bulkForm.count}
                  onChange={(e) => setBulkForm({ ...bulkForm, count: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Passes per Redemption</label>
                <input
                  type="number"
                  min="1"
                  value={bulkForm.quantity}
                  onChange={(e) => setBulkForm({ ...bulkForm, quantity: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max Uses per Coupon</label>
                <input
                  type="number"
                  min="1"
                  value={bulkForm.max_uses}
                  onChange={(e) => setBulkForm({ ...bulkForm, max_uses: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Expires in (days, 0 = never)</label>
                <input
                  type="number"
                  min="0"
                  value={bulkForm.expires_days}
                  onChange={(e) => setBulkForm({ ...bulkForm, expires_days: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-500 text-white py-3 rounded-xl font-semibold"
              >
                Create {bulkForm.count} Coupons
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Redemptions Modal */}
      {showRedemptionsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 w-full max-w-2xl max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">
                Redemptions for {showRedemptionsModal.code}
              </h3>
              <button onClick={() => setShowRedemptionsModal(null)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            {redemptions.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No redemptions yet</p>
            ) : (
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-3">User</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Redeemed At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {redemptions.map(r => (
                    <tr key={r.id}>
                      <td className="p-3">{r.user?.name || r.user?.username || 'Unknown'}</td>
                      <td className="p-3">{r.user?.email || '-'}</td>
                      <td className="p-3">{new Date(r.redeemed_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminP2PCoupons;
